/**
 * Storage backend abstraction — replaces the Python filesystem (users/*.json etc.).
 *
 * The Python data layer reads and writes JSON files under PROJECT_ROOT/users/. The static
 * browser build has no filesystem, so every "path" becomes a logical key into a pluggable
 * backend:
 *   - `LocalStorageBackend` (browser): key = "btr:" + path, value = file content.
 *   - `MemoryBackend` (tests / node): a plain Map.
 *
 * Paths keep the Python relative shapes ("users/<slug>.json", "users/_geocode_cache.json",
 * "openai.json") so `json_store.ts` ports 1:1 against the Python source.
 *
 * NOTE on atomicity: Python's `write_json` wrote a sibling .tmp file and os.replace()d it so
 * a crash could never tear a profile. localStorage writes are already atomic per key (a
 * single `setItem`), so the TS port uses plain writes — there is no temp-file dance and no
 * .tmp keys ever exist.
 */

export interface StorageBackend {
  /** File content at `path`, or null if it does not exist (Python: Path.exists()/read_text). */
  read(path: string): string | null;
  write(path: string, content: string): void;
  remove(path: string): void;
  /** All stored paths that start with `prefix` (Python: dir.glob). Sorted for determinism. */
  list(prefix: string): string[];
}

/** Key prefix that namespaces this app inside the shared browser localStorage. */
export const STORAGE_KEY_PREFIX = "btr:";

export class LocalStorageBackend implements StorageBackend {
  read(path: string): string | null {
    return localStorage.getItem(STORAGE_KEY_PREFIX + path);
  }

  write(path: string, content: string): void {
    localStorage.setItem(STORAGE_KEY_PREFIX + path, content);
  }

  remove(path: string): void {
    localStorage.removeItem(STORAGE_KEY_PREFIX + path);
  }

  list(prefix: string): string[] {
    const out: string[] = [];
    const full = STORAGE_KEY_PREFIX + prefix;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith(full)) out.push(key.slice(STORAGE_KEY_PREFIX.length));
    }
    return out.sort();
  }
}

export class MemoryBackend implements StorageBackend {
  private store = new Map<string, string>();

  read(path: string): string | null {
    const value = this.store.get(path);
    return value === undefined ? null : value;
  }

  write(path: string, content: string): void {
    this.store.set(path, content);
  }

  remove(path: string): void {
    this.store.delete(path);
  }

  list(prefix: string): string[] {
    return [...this.store.keys()].filter((key) => key.startsWith(prefix)).sort();
  }
}

/**
 * Device-local paths — the ONLY things that persist in the browser's localStorage. Everything
 * else (a person's base/run/times profile data) lives in DynamoDB and is held in memory for the
 * active session, so a refresh keeps only the names registry + the device's own settings:
 *   - `settings/...`  — the BYOK OpenAI key, the session-name registry (device-private).
 *   - `openai.json`   — the runtime OpenAI-config override.
 *   - `users/_geocode_cache.json` — a shared place→coords lookup cache (perf, not identity).
 * Note `users/_draft*.json` is NOT device-local (it is per-session draft state → memory).
 */
export function isDeviceLocalPath(path: string): boolean {
  return path.startsWith("settings/") || path === "openai.json" || path === "users/_geocode_cache.json";
}

/**
 * Routes each path to one of two backends by `isLocal(path)`. The browser uses this to keep
 * device-local keys in localStorage while profile data lives only in memory (synced to
 * DynamoDB by session_sync). Transparent to json_store and the worker snapshot/reconcile.
 */
export class CompositeBackend implements StorageBackend {
  constructor(
    private local: StorageBackend,
    private session: StorageBackend,
    private isLocal: (path: string) => boolean = isDeviceLocalPath,
  ) {}

  private pick(path: string): StorageBackend {
    return this.isLocal(path) ? this.local : this.session;
  }

  read(path: string): string | null {
    return this.pick(path).read(path);
  }

  write(path: string, content: string): void {
    this.pick(path).write(path, content);
  }

  remove(path: string): void {
    this.pick(path).remove(path);
  }

  list(prefix: string): string[] {
    return [...new Set([...this.local.list(prefix), ...this.session.list(prefix)])].sort();
  }
}

// ---------------------------------------------------------------------------
// Module-level seam (Python tests monkeypatched paths; TS tests swap the backend)
// ---------------------------------------------------------------------------

let _storage: StorageBackend | null = null;

/**
 * The active backend. In the browser: a CompositeBackend keeping device-local keys
 * (`isDeviceLocalPath`) in localStorage and all profile data in memory (DynamoDB is the
 * cross-session store, hydrated by session_sync). Elsewhere (node/tests): a plain in-memory map.
 */
export function getStorage(): StorageBackend {
  if (_storage === null) {
    _storage =
      typeof localStorage !== "undefined"
        ? new CompositeBackend(new LocalStorageBackend(), new MemoryBackend())
        : new MemoryBackend();
  }
  return _storage;
}

/** Install a backend (tests: `setStorage(new MemoryBackend())` per test). */
export function setStorage(backend: StorageBackend | null): void {
  _storage = backend;
}

// ---------------------------------------------------------------------------
// JSON import/export — move existing users/*.json files into the browser app
// ---------------------------------------------------------------------------

/**
 * All stored user files as { "users/<name>.json": parsed JSON }. Non-JSON entries (e.g. the
 * legacy .log files, never written by this port) are skipped. Use this to download a backup
 * of everything the browser has.
 */
export function exportAllUsers(): Record<string, any> {
  const storage = getStorage();
  const out: Record<string, any> = {};
  for (const path of storage.list("users/")) {
    const raw = storage.read(path);
    if (raw === null) continue;
    try {
      out[path] = JSON.parse(raw);
    } catch {
      // skip non-JSON content
    }
  }
  return out;
}

/**
 * Import files produced by `exportAllUsers()` or copied from the Python repo's users/
 * directory. Keys may be bare filenames ("raahil_attempt_1.json") or full relative paths
 * ("users/raahil_attempt_1.json"); values may be parsed objects or raw JSON strings.
 * Existing entries at the same path are overwritten. Returns the stored paths.
 */
export function importUsers(files: Record<string, any>): string[] {
  const storage = getStorage();
  const written: string[] = [];
  for (const [name, content] of Object.entries(files)) {
    const path = name.startsWith("users/") ? name : `users/${name}`;
    const payload = typeof content === "string" ? content : JSON.stringify(content, null, 2);
    storage.write(path, payload);
    written.push(path);
  }
  return written;
}
